import type { Asset, UnresolvedLink } from 'contentful';
import { normalizeContentfulAssetUrl } from 'libs/contentful/rest/adapter/transforms';
import type { ContentfulAsset, MediaAsset } from 'libs/contentful/types';

/**
 * Flattens a resolved Contentful `Asset` (the raw Entry-native shape) into the
 * flat `MediaAsset` that `<Media>` consumes. Entry-native modules should route
 * every asset field they pass to `<Media>` through this.
 *
 * Returns undefined for unresolvable links: the REST client's default chain
 * leaves links to archived/unpublished assets as raw Link objects rather than
 * removing them, so `fields` is not guaranteed to exist at runtime.
 */
export function toMediaAsset(
  asset: Asset<'WITHOUT_UNRESOLVABLE_LINKS'> | UnresolvedLink<'Asset'>,
): MediaAsset | undefined {
  if (!('fields' in asset)) return undefined;

  const { fields, sys } = asset;
  const file = fields.file;

  return {
    sys: { id: sys.id },
    url: file?.url ? normalizeContentfulAssetUrl(file.url) : '',
    contentType: file?.contentType ?? '',
    title: fields.title ?? '',
    description: fields.description,
    fileName: file?.fileName,
    size: file?.details?.size,
    width: file?.details?.image?.width,
    height: file?.details?.image?.height,
  };
}

export function toContentfulAsset(
  asset: Asset<'WITHOUT_UNRESOLVABLE_LINKS'> | UnresolvedLink<'Asset'> | undefined,
): ContentfulAsset | undefined {
  if (!asset) return undefined;

  const mediaAsset = toMediaAsset(asset);
  if (!mediaAsset) return undefined;

  return {
    sys: { id: asset.sys.id },
    url: mediaAsset.url,
    title: mediaAsset.title,
    description: mediaAsset.description ?? '',
    contentType: mediaAsset.contentType,
    width: mediaAsset.width ?? 0,
    height: mediaAsset.height ?? 0,
  };
}
